/**
 * report/investigationTypes.ts — de onderzoeken die een rapport kan beslaan, en welke
 * GeoVoice vooraf aanvinkt op basis van de data (featureplan §1, hoofdflow stap 1).
 */

import { isInspectiegatType } from '../codelists/index.js';
import { BZB_ASFALT, ASFALT_TYPE_TI } from '../codelists/nen6693.js';
import type { FieldOrder } from '../fieldOrder.js';

export const INVESTIGATION_TYPES = [
  { key: 'soil', label: 'Bodem', norm: 'NEN 5740' },
  { key: 'groundwater', label: 'Grondwater', norm: 'NEN 5740' },
  { key: 'pfas', label: 'PFAS', norm: 'Handelingskader PFAS' },
  { key: 'asbestosSoil', label: 'Asbest in bodem', norm: 'NEN 5707' },
  { key: 'asbestosRubble', label: 'Asbest in puinfundering', norm: 'NEN 5897' },
  { key: 'asphalt', label: 'Asfalt', norm: 'CROW 210' },
  { key: 'crow400', label: 'Veiligheidsklasse', norm: 'CROW 400' },
] as const;

export type InvestigationType = (typeof INVESTIGATION_TYPES)[number]['key'];

const KEYS: ReadonlySet<string> = new Set(INVESTIGATION_TYPES.map((t) => t.key));

export function isInvestigationType(value: unknown): value is InvestigationType {
  return typeof value === 'string' && KEYS.has(value);
}

/** TI-codes van asfalt-BZB's (NEN 5104 slaat de code op, NEN 6693 de term 'asfalt'). */
const ASPHALT_CODES: ReadonlySet<string> = new Set(['AS', ...Object.values(ASFALT_TYPE_TI)]);

type BzbLike = { type?: string | null; code?: string | null; ti_bzb_type?: string | null };
type LayerLike = { bzb?: BzbLike[] | null };

/** Bevat de laag een bijzonder bestanddeel asfalt? Beide standaarden. */
export function isAsphaltLayer(layer: LayerLike | null | undefined): boolean {
  return (layer?.bzb || []).some((b) =>
    b?.type === BZB_ASFALT || ASPHALT_CODES.has(String(b?.code ?? b?.ti_bzb_type ?? '').toUpperCase()));
}

export interface InvestigationSuggestionInput {
  borings: { id: string; name?: string | null; type?: string | null; layers?: LayerLike[] | null }[];
  peilbuizen: { boringId?: string | null }[];
  /** De veldwerkopdracht van het project (normalizeFieldOrder), als die er is. */
  fieldOrder?: FieldOrder | null;
}

/** Waar een voorstel op berust; de dialoog toont het als label. */
export type SuggestionSource = 'standard' | 'fieldOrder' | 'borings';

export interface InvestigationSuggestion {
  key: InvestigationType;
  /** Vooraf aanvinken. */
  suggested: boolean;
  /** Altijd aan (bodem): niet uit te zetten. */
  fixed: boolean;
  /** Waarom, in een zin; null als er niets op wijst. */
  reason: string | null;
  source: SuggestionSource | null;
}

export interface InvestigationSuggestions {
  items: InvestigationSuggestion[];
  /** Tips voor onderzoeken die niet aanstaan maar wel ter sprake komen (puin → asbest in puinfundering). */
  hints: string[];
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const sentence = (parts: string[]) => {
  const s = parts.length <= 1 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} en ${parts[parts.length - 1]}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const namesOf = (list: { name?: string | null }[]) =>
  `${list.slice(0, 4).map((b) => b.name).filter(Boolean).join(', ')}${list.length > 4 ? ' …' : ''}`;

/**
 * Welke onderzoeken GeoVoice vooraf aanvinkt, en waarom. De adviseur bevestigt; dit is een
 * voorstel. De veldwerkopdracht zegt wat er GEVRAAGD is, de boringen wat er GEDAAN is; beide
 * tellen, de opdracht eerst.
 *
 * - Bodem (NEN 5740) altijd.
 * - Grondwater: protocol 2002, peilbuizen of grondwatermonsters in de opdracht; anders een
 *   peilbuis in de boringen.
 * - Asbest in bodem: protocol 2018 of een asbestmonster in de opdracht; anders een inspectiegat.
 * - Asfalt: een laag met het bestanddeel asfalt; anders asfalt in de opdracht.
 * - Veiligheidsklasse (CROW 400): een veiligheidsklasse in de opdracht.
 * - PFAS: PFAS als verwachte stof of als analysepakket in de opdracht.
 * - Asbest in puinfundering alleen als de opdracht hem noemt; wordt er puin verwacht, dan een tip.
 */
export function investigationSuggestions(input: InvestigationSuggestionInput): InvestigationSuggestions {
  const borings = input?.borings || [];
  const peilbuizen = input?.peilbuizen || [];
  const fo = input?.fieldOrder || null;
  const protocols = new Set<string>(fo?.protocols || []);
  const wells = (fo?.work?.wells || []).reduce((n, w) => n + (Number(w.count) || 0), 0);
  // Alles wat de opdracht over monsters en analyses zegt, om op te zoeken.
  const sampling = [...(fo?.sampling?.soil || []), fo?.sampling?.groundwater, fo?.sampling?.asbestos,
    ...(fo?.lab?.packages || []), ...(fo?.remarks || [])].filter(Boolean).join(' ').toLowerCase();
  const expected = [...(fo?.investigation?.expectedContaminants || []), fo?.investigation?.expectedSoil,
    ...(fo?.investigation?.pavements || []), fo?.work?.hardSurface].filter(Boolean).join(' ').toLowerCase();

  const items = new Map<InvestigationType, InvestigationSuggestion>();
  const set = (key: InvestigationType, reason: string | null, source: SuggestionSource | null, fixed = false) =>
    items.set(key, { key, suggested: !!reason, fixed, reason, source: reason ? source : null });

  set('soil', 'Altijd onderdeel van een verkennend bodemonderzoek', 'standard', true);

  const gw: string[] = [];
  if (protocols.has('2002')) gw.push('protocol 2002 aangevinkt');
  if (wells) gw.push(`${plural(wells, 'peilbuis', 'peilbuizen')} opgegeven`);
  if (!gw.length && fo?.sampling?.groundwater) gw.push('grondwatermonsters gevraagd');
  if (gw.length) set('groundwater', sentence(gw), 'fieldOrder');
  else set('groundwater', peilbuizen.length ? `${plural(peilbuizen.length, 'peilbuis', 'peilbuizen')} in het project` : null, 'borings');

  const asb: string[] = [];
  if (protocols.has('2018')) asb.push('protocol 2018 aangevinkt');
  if (/asbest/.test(sampling)) asb.push(/indicatie/.test(sampling) ? 'indicatief asbestmonster gevraagd' : 'asbestmonster gevraagd');
  const pits = borings.filter((b) => isInspectiegatType(b.type));
  if (asb.length) set('asbestosSoil', sentence(asb), 'fieldOrder');
  else set('asbestosSoil', pits.length ? `${plural(pits.length, 'inspectiegat', 'inspectiegaten')} (${namesOf(pits)})` : null, 'borings');

  const asphalt = borings.filter((b) => (b.layers || []).some(isAsphaltLayer));
  if (asphalt.length) set('asphalt', `Asfalt beschreven in ${plural(asphalt.length, 'boring', 'boringen')} (${namesOf(asphalt)})`, 'borings');
  else set('asphalt', /asfalt/.test(expected) ? 'Asfalt genoemd in de veldwerkopdracht' : null, 'fieldOrder');

  const safety = fo?.safety?.safetyClass?.trim();
  set('crow400', safety ? `Veiligheidsklasse ${safety.charAt(0).toLowerCase()}${safety.slice(1)} opgegeven` : null, 'fieldOrder');

  set('pfas', /pfas/.test(`${expected} ${sampling}`) ? 'PFAS genoemd in de veldwerkopdracht' : null, 'fieldOrder');

  const rubble = /puinfundering|nen ?5897/.test(`${expected} ${sampling}`);
  set('asbestosRubble', rubble ? 'Puinfundering genoemd in de veldwerkopdracht' : null, 'fieldOrder');
  const hints = !rubble && /puin/.test(expected)
    ? ['In de veldwerkopdracht staat dat puin wordt verwacht. Overweeg asbest in puinfundering.']
    : [];

  return { items: INVESTIGATION_TYPES.map((t) => items.get(t.key)!), hints };
}

/** De onderzoeken die vooraf aanstaan, in de vaste volgorde (zie investigationSuggestions). */
export function suggestInvestigationTypes(input: InvestigationSuggestionInput): InvestigationType[] {
  return investigationSuggestions(input).items.filter((i) => i.suggested).map((i) => i.key);
}
